import type { FormGroup } from '@angular/forms';
import type { SecurePhone } from '@freelancer/datastore/collections';
import type { FormGroupTypeOf } from '@freelancer/utils';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import type { Observable } from 'rxjs';

export type CredentialsFormGroup = FormGroup<
  FormGroupTypeOf<{
    email: string;
    password: string;
  }>
>;

export type BasicInfoFormGroup = FormGroup<
  FormGroupTypeOf<{
    firstName: string;
    lastName: string;
    username: string;
    termsOfUse: boolean;
    privacyConsent: boolean;
    personalUse: boolean;
  }>
>;

export type CaptchaFormGroup = FormGroup<
  FormGroupTypeOf<{
    response: string;
    challenge: string;
  }>
>;

export interface PhoneVerificationRequest {
  referenceId: string;
  phoneDetails: SecurePhone;
  smsMethod?: PhoneVerificationSMSMethod;
}

export enum PhoneVerificationSMSMethod {
  SMS = 'sms',
  WHATSAPP = 'whatsapp',
}

export const PHONE_VERIFICATION_ERRORS: Record<string, string> = {
  NETWORK_ERROR: $localize`Something went wrong while sending the code. Please try again shortly.`,
  SERVER_ERROR: $localize`Something went wrong on our end. Please try again shortly.`,
  [ErrorCodeApi.PHONE_CANNOT_USE_NUMBER]: $localize`This number cannot be used for verification. Please choose a different one.`,
  [ErrorCodeApi.EMAIL_VERIFICATION_REQUIRED]: $localize`Please verify your email to continue.`,
  [ErrorCodeApi.PHONE_INVALID_NUMBER]: $localize`This number cannot be used for verification. Please choose a different one.`,
  [ErrorCodeApi.PHONE_MAXIMUM_NUMBER_OF_ATTEMPTS_REACHED]: $localize`Too many verification attempts. Please try again later.`,
  [ErrorCodeApi.PHONE_NUMBER_ALREADY_USED]: $localize`This number is already in use. Try a different one.`,
  [ErrorCodeApi.PHONE_SAME_NUMBER]: $localize`That's already your current number. Please enter a different one.`,
  [ErrorCodeApi.TOO_MANY_REQUESTS]: $localize`Too many verification attempts. Please try again later.`,
  [ErrorCodeApi.USER_ACCOUNT_LIMITED]: $localize`You are not allowed to perform this action. Please contact Support.`,
  [ErrorCodeApi.PHONE_COMPLETE_ID_VERIFICATION_REQUIRED]: $localize`Please complete ID verification to continue.`,
  [ErrorCodeApi.PHONE_INVALID_VERIFICATION]: $localize`Invalid verification request.`,
  [ErrorCodeApi.PHONE_INVALID_VERIFICATION_CODE]: $localize`The code you entered is incorrect. Please try again.`,
  [ErrorCodeApi.WHATSAPP_CONSENT_REQUIRED]: $localize`WhatsApp opt-in is required to verify this phone number.`,
  [ErrorCodeApi.WHATSAPP_INVALID_NUMBER]: $localize`Please update your WhatsApp to continue.`,
};

export interface LoadingState {
  isLoading$: Observable<boolean>;
  loadingText$: Observable<string | undefined>;
}

export interface EmailVerificationJwtData {
  user_id: string;
  sub: string;
  iat: number;
}

export const EMAIL_VERIFICATION_TOKEN_SUBJECT = 'new_account_verification';

export enum NewAccountPageSource {
  POST_PROJECT = 'post_project',
}
